import type { Npc } from '../../types/progression'

/**
 * NPC de las guías de progresión, identificados por su ID en 06_monsters.
 * Identidad ≠ ubicación: la ubicación se resuelve en utils/npcLocation.ts con este orden:
 * administración → apariciones permanentes configuradas → referencia (con fuente) → no documentada.
 * No confundir con monstruos de nombre parecido (Goblin, Werewolf).
 *
 * Las referencias son de MU Online Season 6 clásico y quedan "pendientes de validar en Vanguard".
 */
export const npcs: Npc[] = [
  {
    id: 'sebina',
    name: 'Sebina the Priest',
    monsterId: 235,
    role: 'Segunda clase (misión de cambio de clase)',
    aliases: ['sebina', 'sebina the priest', 'sacerdotisa', 'sacerdotisa sebina', 'priestess sebina'],
    classicMaps: ['Devias'],
    reference: {
      points: [{ mapName: 'Devias', x: 183, y: 32 }],
      source: 'Mu Online Guide — Quest 2nd Class: Sebina The Priest',
      url: 'https://guidemuonline.com/guides/class-change/quest-2nd-class-sebina-the-priest',
    },
    sectionPath: '/guia/evoluciones#sebina',
  },
  {
    id: 'marlon',
    name: 'Marlon',
    monsterId: 229,
    role: 'Misión de Marlon y desbloqueo del combo',
    aliases: ['marlon', 'quest marlon', 'mision marlon'],
    classicMaps: ['Lorencia', 'Devias', 'Noria', 'Atlans'],
    reference: {
      points: [
        { mapName: 'Lorencia', x: 137, y: 87 },
        { mapName: 'Devias', x: 198, y: 47 },
        { mapName: 'Noria', x: 169, y: 89 },
        { mapName: 'Atlans', x: 17, y: 25 },
      ],
      condition: 'Se desplaza entre estos cuatro mapas: está en uno de ellos a la vez, no en todos.',
      source: 'Mu Online Guide — Quest 2nd Class: Marlon',
      url: 'https://guidemuonline.com/guides/class-change/quest-marlon',
    },
    sectionPath: '/guia/evoluciones#marlon',
  },
  {
    id: 'devin',
    name: 'Apostle Devin',
    monsterId: 406,
    role: 'Tercera evolución',
    aliases: ['devin', 'apostle devin', 'apostol devin'],
    classicMaps: ['Crywolf'],
    reference: {
      points: [{ mapName: 'Crywolf', x: 228, y: 48 }],
      source: 'MU Online News — [Quest 3rd Class] Apostle Devin',
      url: 'https://muonline.news/game-guides/class-change/quest-3rd-class-apostle-devin',
    },
    sectionPath: '/guia/evoluciones#tercera',
  },
  {
    id: 'quarel',
    name: 'Werewolf Quarel',
    monsterId: 407,
    role: 'Tercera evolución · paso hacia Barracks',
    aliases: ['werewolf quarel', 'werewolf quarrel', 'quarel', 'quarrel', 'npc barracks', 'acceso barracks'],
    classicMaps: ['Crywolf'],
    reference: {
      points: [{ mapName: 'Crywolf', x: 63, y: 239 }],
      source: 'Mu Online Guides — 3rd Class Quest',
      url: 'https://www.guidesmu.com/3rd-class-quest/',
    },
    sectionPath: '/guia/evoluciones#tercera-etapa-2',
  },
  {
    id: 'gatekeeper',
    name: 'Gatekeeper',
    monsterId: 408,
    role: 'Tercera evolución · paso hacia Refuge',
    aliases: ['gatekeeper', 'gate keeper', 'guardian de la puerta', 'npc refuge', 'acceso refuge'],
    classicMaps: ['Barracks'],
    reference: {
      points: [{ mapName: 'Barracks', x: 120, y: 168 }],
      source: 'Mu Online Guides — 3rd Class Quest',
      url: 'https://www.guidesmu.com/3rd-class-quest/',
    },
    sectionPath: '/guia/evoluciones#tercera-etapa-3',
  },
  {
    id: 'chaos-goblin',
    name: 'Chaos Goblin',
    monsterId: 238,
    role: 'Combinaciones de la Chaos Machine (armas Chaos, alas, capas)',
    aliases: ['chaos goblin', 'chaos machine', 'maquina chaos', 'maquina del chaos', 'goblin del chaos'],
    classicMaps: ['Noria'],
    // Solo el mapa: no se encontró una coordenada Season 6 documentada compatible.
    reference: {
      points: [{ mapName: 'Noria' }],
      source: 'Mu Online Guide — Noria',
      url: 'https://guidemuonline.com/maps/noria',
    },
    sectionPath: '/guia/alas',
  },
  {
    id: 'pet-trainer',
    name: 'Trainer',
    // Confirmado por el staff: "Pet Trainer" y "Trainer" son el mismo NPC; en la guía se llama Trainer (226).
    monsterId: 226,
    role: 'Resurrección de espíritus: Dark Horse y Dark Raven',
    aliases: ['pet trainer', 'trainer', 'entrenador', 'entrenador de mascotas', 'resurrect spirit', 'resucitar espiritu'],
    classicMaps: ['Lorencia'],
    reference: { points: [{ mapName: 'Lorencia', x: 122, y: 110 }], source: 'NPC de MU Online — Webzen' },
    sectionPath: '/guia/combinaciones#mascotas',
  },
]

export const getNpc = (id: string) => npcs.find((n) => n.id === id)
